import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminPressReleases = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_published: '',
    category: '',
    search: '',
    ...initialFilters
  });

  const cleanFilters = useMemo(() => {
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
    if (!cleaned.per_page) {
      cleaned.per_page = 10;
    }
    return cleaned;
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: releasesResponse, isLoading, error } = useQuery({
    queryKey: ['adminPressReleases', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/press-releases${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const { data: statisticsResponse } = useQuery({
    queryKey: ['adminPressReleaseStatistics'],
    queryFn: async () => {
      const response = await api.get('/admin/press-releases/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/press-releases', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminPressReleases']);
      queryClient.invalidateQueries(['adminPressReleaseStatistics']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/press-releases/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminPressReleases']);
      queryClient.invalidateQueries(['adminPressReleaseStatistics']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/press-releases/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminPressReleases']);
      queryClient.invalidateQueries(['adminPressReleaseStatistics']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/press-releases/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminPressReleases']);
      queryClient.invalidateQueries(['adminPressReleaseStatistics']);
    }
  });

  const releases = useMemo(() => {
    if (releasesResponse?.data?.data) {
      return releasesResponse.data.data;
    }
    return releasesResponse?.data || [];
  }, [releasesResponse]);

  const statistics = useMemo(() => {
    return statisticsResponse?.data || {
      total_releases: 0,
      published_releases: 0,
      draft_releases: 0,
      total_views: 0
    };
  }, [statisticsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return {
    releases,
    isLoading,
    error,
    filters,
    handleFilterChange,
    statistics,
    createRelease: (data, options) => createMutation.mutate(data, options),
    isCreating: createMutation.isPending,
    updateRelease: (data, options) => updateMutation.mutate(data, options),
    isUpdating: updateMutation.isPending,
    toggleStatus: (id, options) => toggleStatusMutation.mutate(id, options),
    isToggling: toggleStatusMutation.isPending,
    deleteRelease: (id, options) => deleteMutation.mutate(id, options),
    isDeleting: deleteMutation.isPending
  };
};

